import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import SaveIcon from '@mui/icons-material/Save'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { useMutation } from '@apollo/client'
import { createSystemConfig } from '../../graphql/mutation'

import {
  Box,
  Typography,
  Button,
  TextField,
  Alert,
  MenuItem,
  Select,
  FormControl,
  Snackbar,
  CircularProgress,
  Paper,
  InputLabel,
} from '@mui/material'

const providerOptions = [
  { value: 'FAL', name: 'FAL' },
  { value: 'WIRO', name: 'WIRO' },
  { value: 'SERVICE', name: 'SERVICE' },
]

const toolTypeOptions = [
  { value: 'IMG2IMG', name: 'IMG2IMG' },
  { value: 'IMG2VIDEO', name: 'IMG2VIDEO' },
  { value: 'IMG2VIDEO_WITH_TEXT', name: 'IMG2VIDEO_WITH_TEXT' },
  { value: 'PREDICT_WITH_MODEL', name: 'PREDICT_WITH_MODEL' },
  { value: 'TEXT2VIDEO', name: 'TEXT2VIDEO' },
  { value: 'TRAIN', name: 'TRAIN' },
  { value: 'VIDEO2VIDEO', name: 'VIDEO2VIDEO' },
]

export default function CreateSystemPage() {
  const navigate = useNavigate()

  const [systemConfig, setSystemConfig] = useState({
    provider: '',
    systemType: '',
    systemModel: '',
  })

  const [progress, setProgress] = useState(false)
  const [snackBarOpen, setSnackBarOpen] = useState(false)
  const [snackBarMessage, setSnackBarMessage] = useState(null)
  const [snackBarSeverity, setSnackBarSeverity] = useState('success')

  const [createSystemConfigMutation] = useMutation(createSystemConfig)

  const handleChange = e => {
    const { name, value } = e.target
    setSystemConfig(prev => ({ ...prev, [name]: value }))
  }

  const handleSnackBarClose = () => {
    setSnackBarOpen(false)
  }

  const handleCreateSystemConfig = async () => {
    // Validation
    if (!systemConfig.provider) {
      setSnackBarMessage('Provider is required')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    if (!systemConfig.systemType) {
      setSnackBarMessage('System Type is required')
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
      return
    }

    setProgress(true)
    try {
      const response = await createSystemConfigMutation({
        variables: {
          provider: systemConfig.provider,
          systemType: systemConfig.systemType,
          systemModel: systemConfig.systemModel || null,
        },
      })
      
      if (response.data) {
        setSnackBarMessage('System configuration created successfully')
        setSnackBarSeverity('success')
        setSnackBarOpen(true)
        setTimeout(() => {
          navigate('/system')
        }, 1000)
      }
    } catch (error) {
      console.error('Error creating system config:', error)
      setSnackBarMessage(`Creation failed: ${error.message}`)
      setSnackBarSeverity('error')
      setSnackBarOpen(true)
    } finally {
      setProgress(false)
    }
  }

  const handleGoBack = () => {
    navigate('/system')
  }

  return (
    <Box>
      <Snackbar
        open={snackBarOpen}
        autoHideDuration={3000}
        onClose={handleSnackBarClose}
        message={snackBarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackBarClose} 
          severity={snackBarSeverity} 
          sx={{ width: '100%' }}
        >
          {snackBarMessage}
        </Alert>
      </Snackbar>

      {/* Sticky Header with Actions */}
      <Box 
        sx={{ 
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          backgroundColor: 'white',
          borderBottom: '1px solid #e9ecef',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 4, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant='h4' sx={{ color: '#212529', fontWeight: 'bold' }}>
            Create System Configuration
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              onClick={handleGoBack}
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              sx={{ 
                borderRadius: 2,
                px: 3,
                borderColor: '#6c757d',
                color: '#6c757d',
                '&:hover': { 
                  backgroundColor: '#6c757d', 
                  color: 'white',
                  borderColor: '#6c757d'
                }
              }}
            >
              Back to List
            </Button>
            <Button
              onClick={handleCreateSystemConfig}
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              disabled={progress}
              sx={{ 
                borderRadius: 2,
                px: 4,
                backgroundColor: '#28a745',
                '&:hover': { backgroundColor: '#218838' }
              }}
            >
              Create Configuration
            </Button>
          </Box>
        </Box>
      </Box>

      {progress && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(255,255,255,0.7)',
            zIndex: 1300,
          }}
        >
          <CircularProgress size={80} thickness={5} />
        </Box>
      )}

      {/* Main Content Container */}
      <Box sx={{ maxWidth: '1400px', mx: 'auto', px: { xs: 2, md: 4 } }}>
        {/* Basic Information Section */}
        <Paper sx={{ p: 4, mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', color: '#2c3e50', borderBottom: '2px solid #28a745', pb: 1 }}>
            New System Configuration
          </Typography>
          
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mb: 3 }}>
            <FormControl fullWidth required>
              <InputLabel>Provider *</InputLabel>
              <Select 
                name="provider" 
                onChange={handleChange} 
                value={systemConfig.provider}
                label="Provider *"
                sx={{ borderRadius: 2 }}
              >
                {providerOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>System Type *</InputLabel>
              <Select 
                name="systemType" 
                onChange={handleChange} 
                value={systemConfig.systemType}
                label="System Type *"
                sx={{ borderRadius: 2 }}
              >
                {toolTypeOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <TextField
            fullWidth
            label="System Model"
            variant="outlined"
            name="systemModel"
            value={systemConfig.systemModel}
            onChange={handleChange}
            placeholder="Enter system model name (optional)"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            helperText="Optional field for specifying the system model"
          />
        </Paper>

        {/* Guidelines Section */}
        <Paper sx={{ p: 4, mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', backgroundColor: '#f8f9fa' }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: '#495057' }}>
            Configuration Guidelines
          </Typography>
          
          <Box component="ul" sx={{ pl: 2, color: '#6c757d' }}>
            <li>Provider and System Type combination must be unique</li>
            <li>Provider determines which service will handle the requests</li>
            <li>System Type defines what kind of operation this configuration supports</li>
            <li>System Model is optional and can be used to specify a particular model version</li>
            <li>All fields except System Model are required</li>
          </Box>
        </Paper>
      </Box>
    </Box>
  )
}
